import { useEffect } from "react";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "@/lib/seo";

// Runtime per-route SEO: sets title, meta description, canonical, OG/Twitter tags
// and injects page-level JSON-LD. Cleans up JSON-LD on route change.
const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const match = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const Seo = ({ title, description, path = "/", image, type = "website", noindex = false, jsonLd = [] }) => {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;

    document.title = title;
    upsertMeta('meta[name="description"]', { content: description });
    upsertMeta('meta[name="robots"]', { content: noindex ? "noindex, nofollow" : "index, follow" });

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Open Graph
    upsertMeta('meta[property="og:title"]', { content: title });
    upsertMeta('meta[property="og:description"]', { content: description });
    upsertMeta('meta[property="og:url"]', { content: url });
    upsertMeta('meta[property="og:type"]', { content: type });
    upsertMeta('meta[property="og:image"]', { content: image || DEFAULT_OG_IMAGE });
    upsertMeta('meta[property="og:site_name"]', { content: SITE_NAME });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { content: title });
    upsertMeta('meta[name="twitter:description"]', { content: description });
    upsertMeta('meta[name="twitter:image"]', { content: image || DEFAULT_OG_IMAGE });

    // Page-level JSON-LD (clean up previous route's tags first)
    document.head.querySelectorAll('script[data-seo-page="true"]').forEach((s) => s.remove());
    jsonLd.forEach((data) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-page", "true");
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      document.head.querySelectorAll('script[data-seo-page="true"]').forEach((s) => s.remove());
    };
  }, [title, description, path, image, type, noindex, jsonLd]);

  return null;
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const productLd = (p) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: p.name.de,
  description: p.short.de,
  image: `${SITE_URL}/og/product-${p.id}.png`,
  brand: { "@type": "Brand", name: SITE_NAME },
  sku: p.id,
  url: `${SITE_URL}/product/${p.id}`,
  offers: {
    "@type": "Offer",
    url: `${SITE_URL}/product/${p.id}`,
    price: p.price,
    priceCurrency: "EUR",
    availability: p.soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
  },
});

export default Seo;
